import { fetch_requestor_application_audit_logs, fetch_requestor_application_assigned_users, fetch_requestor_application_documents, fetch_requestor_application_forms, fetch_requestor_application_journals, fetch_requestor_application_notes, fetch_requestor_entity_profiles } from '@/services/requestor_portal_service';
import { listXVerdictEntityConfigs } from '@/services/xverdict_entity_config_service';
import { mapEntityConfigToEntityType } from '@/services/xverdict_config_mapper';
import { application_opened_at_from_application_forms, audit_log_matches_application, audit_timestamp_value, case_fact_columns_from_application_forms, domain_label_from_application_forms, entity_type_id_from_application_forms, entity_type_label, error_message, is_note_journal, is_reasoning_journal, normalize_audit_row, normalize_document, normalize_note, normalize_time_journal, profile_case_description, profile_opened_at, profile_submitter_name, submitted_case_context_parts, submitter_name_from_application_forms, to_array, what_happened_answer_from_application_forms } from './view_case_details_helpers';
import { set_backend_ids_from_forms } from './view_case_details_risk_methods';

export default {
  async resolve_entity_type_label(application_forms = []) {
    const direct_label = domain_label_from_application_forms(application_forms);
    if (direct_label) {
      return direct_label;
    }

    return resolve_entity_type_label_from_config(this, application_forms);
  },
  async resolve_submitter_name(application_forms = []) {
    const direct_name = submitter_name_from_application_forms(application_forms);
    if (direct_name) {
      return direct_name;
    }

    return resolve_profile_submitter_name(this);
  },
  async resolve_case_profile() {
    return resolve_case_profile_for(this);
  },
  async load_case_facts() {
    reset_case_facts(this);
    this.loading_case_facts = true;

    try {
      if (!this.selected_application_id) {
        await load_profile_case_facts(this);
        return;
      }

      await load_application_case_facts(this, this.selected_application_id);
    } catch (error) {
      this.case_facts_error = error_message(error, 'Failed to load case facts.');
      this.backend_fact_columns = [];
      this.backend_application_forms = [];
    } finally {
      this.loading_case_facts = false;
    }
  },
  async load_documents() {
    await load_collection(this, {
      loading_key: 'loading_documents',
      error_key: 'documents_error',
      target_key: 'backend_documents',
      fetcher: fetch_requestor_application_documents,
      mapper: map_documents,
      error_text: 'Failed to load documents.',
    });
  },
  async load_time_journals() {
    await load_collection(this, {
      loading_key: 'loading_time_entries',
      error_key: 'time_entries_error',
      target_key: 'backend_time_entries',
      fetcher: fetch_requestor_application_journals,
      mapper: map_time_journals,
      error_text: 'Failed to load time journals.',
    });
  },
  async load_notes() {
    reset_collection(this, {
      loading_key: 'loading_notes',
      error_key: 'notes_error',
      target_key: 'backend_notes',
    });
    if (!this.selected_application_id) {
      return;
    }

    this.loading_notes = true;
    try {
      const records = await fetch_requestor_application_notes(this.selected_application_id);
      this.backend_notes = map_notes(records);
      this.backend_reasoning_items = map_reasoning_items(records);
      this.action_completed = payout_override_was_authorised(records);
    } catch (error) {
      this.notes_error = error_message(error, 'Failed to load notes.');
      this.backend_notes = [];
      this.backend_reasoning_items = [];
    } finally {
      this.loading_notes = false;
    }
  },
  async load_audit_logs() {
    reset_audit_logs(this);
    if (!has_audit_lookup(this)) {
      return;
    }

    await load_audit_logs_for(this);
  },
  async load_assigned_users() {
    this.backend_assigned_users = [];
    if (!this.selected_application_id) {
      return;
    }

    try {
      this.backend_assigned_users = await fetch_requestor_application_assigned_users(this.selected_application_id);
    } catch {
      this.backend_assigned_users = [];
    }
  },
};

async function resolve_entity_type_label_from_config(component, application_forms = []) {
  const entity_type_id = component.selected_entity_type_id || entity_type_id_from_application_forms(application_forms);
  if (!entity_type_id) {
    return '';
  }

  try {
    const entity_types = (await listXVerdictEntityConfigs()).map(mapEntityConfigToEntityType).filter(Boolean);
    return entity_type_label(to_array(entity_types).find((item) => Number(item?.id) === Number(entity_type_id)));
  } catch {
    return '';
  }
}

async function resolve_profile_submitter_name(component) {
  if (!component.selected_entity_profile_id) {
    return '';
  }

  return profile_submitter_name(await resolve_case_profile_for(component));
}

async function resolve_case_profile_for(component) {
  if (!component.selected_entity_profile_id) {
    return null;
  }

  try {
    const profiles = await fetch_requestor_entity_profiles({
      entity_profile_id: component.selected_entity_profile_id,
      page_size: 1,
      page: 1,
    });
    return to_array(profiles)[0] || null;
  } catch {
    return null;
  }
}

function reset_case_facts(component) {
  component.loading_case_facts = false;
  component.case_facts_error = '';
  component.backend_fact_columns = [];
  component.backend_application_forms = [];
  component.editing_fact_section_key = '';
  component.editing_fact_answers = [];
  component.saving_fact_section_key = '';
  component.fact_edit_error = '';
  component.fact_validation_errors = [];
  component.case_form_submission_error = '';
  component.case_form_submission_success = '';
  component.verification_checks = [];
  component.loading_verification_checks = false;
  component.running_verification_keys = [];
  component.verification_error = '';
  component.clear_fact_edit_success();
  component.backend_domain_label = '';
  component.backend_submitter_name = '';
  component.backend_what_happened_answer = '';
  component.backend_case_context_parts = [];
  component.backend_case_opened_at = '';
}

async function load_profile_case_facts(component) {
  const case_profile = await component.resolve_case_profile();
  component.backend_domain_label = await component.resolve_entity_type_label([]);
  component.backend_submitter_name = profile_submitter_name(case_profile);
  component.backend_what_happened_answer = profile_case_description(case_profile);
  component.backend_case_opened_at = profile_opened_at(case_profile);
}

async function load_application_case_facts(component, application_id) {
  await apply_application_case_facts(component, application_id);
}

async function apply_application_case_facts(component, application_id) {
  const application_forms = await fetch_requestor_application_forms(application_id);
  component.backend_application_forms = application_forms;
  set_backend_ids_from_forms(component, application_forms);
  const case_profile = await component.resolve_case_profile();
  component.backend_domain_label = await component.resolve_entity_type_label(application_forms);
  component.backend_case_opened_at = application_opened_at_from_application_forms(application_forms)
    || profile_opened_at(case_profile);
  component.backend_submitter_name = submitter_name_from_application_forms(application_forms) || profile_submitter_name(case_profile);
  component.backend_what_happened_answer = what_happened_answer_from_application_forms(application_forms) || profile_case_description(case_profile);
  component.backend_case_context_parts = submitted_case_context_parts(application_forms, [
    component.backend_submitter_name,
    component.backend_what_happened_answer,
  ]);
  component.backend_fact_columns = case_fact_columns_from_application_forms(application_forms);
  component.open_first_incomplete_fact_section();
  await component.load_case_verifications();
}

async function load_collection(component, options) {
  reset_collection(component, options);
  if (!component.selected_application_id) {
    return;
  }

  await fetch_collection(component, options);
}

function reset_collection(component, { loading_key, error_key, target_key }) {
  component[loading_key] = false;
  component[error_key] = '';
  component[target_key] = [];
  if (target_key === 'backend_notes') {
    component.backend_reasoning_items = [];
  }
}

async function fetch_collection(component, options) {
  component[options.loading_key] = true;
  try {
    const records = await options.fetcher(component.selected_application_id);
    component[options.target_key] = options.mapper(records);
  } catch (error) {
    component[options.error_key] = error_message(error, options.error_text);
    component[options.target_key] = [];
  } finally {
    component[options.loading_key] = false;
  }
}

function map_documents(documents = []) {
  return documents.map(normalize_document).filter((document) => document.name || document.url);
}

function map_time_journals(journals = []) {
  return journals.filter((journal, index) => {
    const entry = normalize_time_journal(journal, index);
    return entry.activity || entry.hours_value;
  });
}

function map_notes(notes = []) {
  return notes
    .filter(is_note_journal)
    .map((note, index) => ({
      ...normalize_note(note, index),
      action_created_at: note?.created_at || note?.date_created || note?.timestamp || '',
    }))
    .filter((note) => note.body);
}

function payout_override_was_authorised(notes = []) {
  const action_notes = notes
    .map((note, index) => ({
      body: normalize_note(note).body.toLowerCase(),
      timestamp: note?.created_at || note?.date_created || note?.timestamp || '',
      index,
    }))
    .filter((note) =>
      note.body.includes('authorise payout (override) confirmed')
      || note.body.includes('authorise payout confirmed')
      || note.body.includes('payout override undone')
      || note.body.includes('payout authorisation undone')
    );

  if (!action_notes.length) {
    return false;
  }

  const sorted_action_notes = action_notes
    .slice()
    .sort((left, right) => {
      const left_time = Date.parse(left.timestamp);
      const right_time = Date.parse(right.timestamp);
      if (!Number.isNaN(left_time) && !Number.isNaN(right_time) && left_time !== right_time) {
        return left_time - right_time;
      }
      return left.index - right.index;
    });
  const latest = sorted_action_notes[sorted_action_notes.length - 1];

  return latest.body.includes('authorise payout (override) confirmed')
    || latest.body.includes('authorise payout confirmed');
}

function map_reasoning_items(notes = []) {
  return notes.filter(is_reasoning_journal);
}

function reset_audit_logs(component) {
  component.loading_audit_logs = false;
  component.audit_logs_error = '';
  component.backend_audit_rows = [];
}

function has_audit_lookup(component) {
  return component.selected_application_id || component.selected_entity_profile_id;
}

async function load_audit_logs_for(component) {
  component.loading_audit_logs = true;
  try {
    const params = audit_log_params(component);
    const logs = await fetch_requestor_application_audit_logs(params);
    component.backend_audit_rows = map_audit_logs(component, logs);
    if (!component.backend_case_opened_at) {
      component.backend_case_opened_at = earliest_audit_timestamp(logs, component);
    }
  } catch (error) {
    component.audit_logs_error = error_message(error, 'Failed to load audit logs.');
    component.backend_audit_rows = [];
  } finally {
    component.loading_audit_logs = false;
  }
}

function audit_log_params(component) {
  return component.selected_entity_profile_id
    ? { entity_profile_id: component.selected_entity_profile_id }
    : {};
}

function earliest_audit_timestamp(logs = [], component) {
  return logs
    .filter((log) => component.selected_entity_profile_id || audit_log_matches_application(log, component.selected_application_id))
    .map((log) => ({
      value: log?.timestamp || log?.date_created || log?.created_at,
      sort_time: audit_timestamp_value(log),
    }))
    .filter((item) => item.value && item.sort_time)
    .sort((left, right) => left.sort_time - right.sort_time)[0]?.value || '';
}

function map_audit_logs(component, logs = []) {
  return logs
    .filter((log) => component.selected_entity_profile_id || audit_log_matches_application(log, component.selected_application_id))
    .map(normalize_audit_row)
    .filter((row) => row.event)
    .sort((left, right) => Number(right.sort_time || 0) - Number(left.sort_time || 0));
}
